import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { listClientOpportunities, getClientOpportunitiesExportUrl } from "../../api/clientOpportunitiesApi";
import { getClientPortalView } from "../../api/clientPortalApi";
import {
  ClientPortalEmpty,
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate, formatStatusLabel } from "../../utils/clientPortalFormat";
import {
  formatMidpointMediaOpportunityStatus,
  isHiringOpportunity,
  isMidpointMediaView,
} from "../../utils/clientViewTheme";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "updated", label: "Last updated" },
  { value: "follow_up", label: "Next follow-up" },
  { value: "status", label: "Status" },
  { value: "type", label: "Opportunity type" },
];

const PIPELINE_STAGES = [
  { key: "new", label: "New", color: "#2563eb" },
  { key: "assigned", label: "Assigned", color: "#7c3aed" },
  { key: "contacted", label: "Contacted", color: "#0891b2" },
  { key: "under_contract", label: "Under Contract", color: "#d97706" },
  { key: "closed_won", label: "Closed / Won", color: "#16a34a" },
];

const MM_PIPELINE_STAGES = [
  { key: "new", label: "New", color: "#2563eb" },
  { key: "assigned", label: "Reviewing", color: "#7c3aed" },
  { key: "contacted", label: "Contacted", color: "#0891b2" },
  { key: "under_contract", label: "In progress", color: "#d97706" },
  { key: "closed_won", label: "Hired / Won", color: "#16a34a" },
];

const MM_TYPE_FILTERS = [
  { value: "job_application", label: "Job application" },
  { value: "Venezuela Careers Application", label: "Venezuela careers" },
  { value: "Hiring", label: "Hiring" },
  { value: "Consulting", label: "Consulting" },
  { value: "Account", label: "Account" },
];

const DEFAULT_TYPE_FILTERS = [
  { value: "schedule_showing", label: "Schedule Showing" },
  { value: "general_inquiry", label: "General Inquiry" },
  { value: "agent_match", label: "Agent Match" },
  { value: "contact_form", label: "Contact Form" },
];

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "new") return "opp-badge--blue";
  if (s === "assigned" || s === "qualified") return "opp-badge--purple";
  if (s === "contacted") return "opp-badge--cyan";
  if (s === "under_contract") return "opp-badge--amber";
  if (s === "closed_won" || s === "won" || s === "completed") return "opp-badge--green";
  if (s === "on_hold") return "opp-badge--muted";
  if (s === "cancelled" || s === "lost" || s === "failed") return "opp-badge--red";
  return "opp-badge--muted";
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return formatClientDate(dateStr);
}

function isOverdue(followUp) {
  if (!followUp) return false;
  return new Date(followUp) < new Date();
}

const ClientOpportunitiesListPage = () => {
  const { clientViewId } = useParams();
  const [clientView, setClientView] = useState(null);
  const [viewName, setViewName] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [opportunityType, setOpportunityType] = useState("");
  const [needsFollowUp, setNeedsFollowUp] = useState(false);
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [activeView, setActiveView] = useState("dashboard");
  const paths = useClientPortalPaths();

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [viewBody, listBody] = await Promise.all([
        getClientPortalView(clientViewId),
        listClientOpportunities(clientViewId, {
          page,
          pageSize: 25,
          search: search || undefined,
          status: status || undefined,
          opportunityType: opportunityType || undefined,
          needsFollowUp: needsFollowUp || undefined,
          sort,
        }),
      ]);
      setClientView(viewBody.clientView || null);
      setViewName(viewBody.clientView?.name || "Client View");
      setData(listBody);
    } catch (error) {
      setErrorMessage(error.message || "We could not load opportunities. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, page, search, status, opportunityType, needsFollowUp, sort]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;
  const opportunities = data?.opportunities || [];
  const isMidpointMedia = isMidpointMediaView(clientView);
  const pipelineStages = isMidpointMedia ? MM_PIPELINE_STAGES : PIPELINE_STAGES;
  const typeFilters = isMidpointMedia ? MM_TYPE_FILTERS : DEFAULT_TYPE_FILTERS;
  const statusLabel = (status) =>
    isMidpointMedia ? formatMidpointMediaOpportunityStatus(status) : formatStatusLabel(status);

  const urgentItems = useMemo(() => {
    if (!opportunities.length) return [];
    return opportunities
      .filter((o) => {
        const s = String(o.status || "").toLowerCase();
        if (s === "closed_won" || s === "won" || s === "completed" || s === "cancelled" || s === "lost") return false;
        if (o.nextFollowUpAt && isOverdue(o.nextFollowUpAt)) return true;
        if (s === "new") return true;
        return false;
      })
      .slice(0, 5);
  }, [opportunities]);

  const recentItems = useMemo(() => {
    return [...opportunities]
      .sort((a, b) => new Date(b.lastActivityAt || b.submittedAt) - new Date(a.lastActivityAt || a.submittedAt))
      .slice(0, 5);
  }, [opportunities]);

  const exportUrl = getClientOpportunitiesExportUrl(clientViewId, {
    search: search || undefined,
    status: status || undefined,
    opportunityType: opportunityType || undefined,
    needsFollowUp: needsFollowUp || undefined,
    sort,
  });

  return (
    <>
      {/* Header */}
      <section className="panel-card">
        <p className="eyebrow">
          <Link to={paths.view(clientViewId)}>{viewName}</Link> / Opportunities &amp; Leads
        </p>
        <div className="opp-dash-header">
          <div>
            <h2>Opportunities &amp; Leads</h2>
            <p className="muted">
              {isMidpointMedia
                ? "Work every application and inbound inquiry from first contact through hire or close."
                : "Track, prioritize, and act on every lead and opportunity from your public site."}
            </p>
          </div>
          <div className="opp-dash-header-actions">
            <a href={exportUrl} className="btn btn-secondary" download>
              Export CSV
            </a>
          </div>
        </div>
      </section>

      {loading && !data ? <ClientPortalLoading message="Loading opportunities…" /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} /> : null}

      {data ? (
        <>
          {/* KPI Strip */}
          <div className="opp-kpi-strip">
            <button
              type="button"
              className={`opp-kpi-card${!status ? " opp-kpi-card--active" : ""}`}
              onClick={() => { setStatus(""); setPage(1); }}
            >
              <div className="opp-kpi-value">{summary?.total ?? 0}</div>
              <div className="opp-kpi-label">Total</div>
              <div className="opp-kpi-accent" style={{ background: "var(--cp-primary)" }} />
            </button>
            <button
              type="button"
              className={`opp-kpi-card${status === "new" ? " opp-kpi-card--active" : ""}${(summary?.new || 0) > 0 ? " opp-kpi-card--attention" : ""}`}
              onClick={() => { setStatus(status === "new" ? "" : "new"); setPage(1); }}
            >
              <div className="opp-kpi-value">{summary?.new ?? 0}</div>
              <div className="opp-kpi-label">New</div>
              <div className="opp-kpi-accent" style={{ background: "#2563eb" }} />
              {(summary?.new || 0) > 0 && <span className="opp-kpi-pulse" />}
            </button>
            <button
              type="button"
              className={`opp-kpi-card${needsFollowUp ? " opp-kpi-card--active" : ""}${(summary?.needsFollowUp || 0) > 0 ? " opp-kpi-card--warn" : ""}`}
              onClick={() => { setNeedsFollowUp(!needsFollowUp); setStatus(""); setPage(1); }}
            >
              <div className="opp-kpi-value">{summary?.needsFollowUp ?? 0}</div>
              <div className="opp-kpi-label">Needs Follow-Up</div>
              <div className="opp-kpi-accent" style={{ background: "#dc2626" }} />
              {(summary?.needsFollowUp || 0) > 0 && <span className="opp-kpi-dot-warn" />}
            </button>
            <button
              type="button"
              className={`opp-kpi-card${status === "contacted" ? " opp-kpi-card--active" : ""}`}
              onClick={() => { setStatus(status === "contacted" ? "" : "contacted"); setPage(1); }}
            >
              <div className="opp-kpi-value">{summary?.contacted ?? 0}</div>
              <div className="opp-kpi-label">Contacted</div>
              <div className="opp-kpi-accent" style={{ background: "#0891b2" }} />
            </button>
            <button
              type="button"
              className={`opp-kpi-card${status === "closed_won" ? " opp-kpi-card--active" : ""}`}
              onClick={() => { setStatus(status === "closed_won" ? "" : "closed_won"); setPage(1); }}
            >
              <div className="opp-kpi-value">{summary?.closedWon ?? 0}</div>
              <div className="opp-kpi-label">{isMidpointMedia ? "Hired / Won" : "Closed / Won"}</div>
              <div className="opp-kpi-accent" style={{ background: "#16a34a" }} />
            </button>
          </div>

          {/* Pipeline Visual */}
          {(summary?.total || 0) > 0 && (
            <section className="panel-card opp-pipeline-section">
              <h3 className="opp-section-heading">Pipeline</h3>
              <div className="opp-pipeline">
                {pipelineStages.map((stage) => {
                  const count = summary?.[stage.key] ?? (stage.key === "under_contract" ? summary?.underContract : 0) ?? 0;
                  const pct = summary?.total > 0 ? Math.max(2, (count / summary.total) * 100) : 0;
                  return (
                    <button
                      key={stage.key}
                      type="button"
                      className={`opp-pipeline-stage${status === stage.key ? " is-active" : ""}`}
                      onClick={() => { setStatus(status === stage.key ? "" : stage.key); setPage(1); }}
                    >
                      <div className="opp-pipeline-bar" style={{ width: `${pct}%`, background: stage.color }} />
                      <div className="opp-pipeline-info">
                        <span className="opp-pipeline-count" style={{ color: stage.color }}>{count}</span>
                        <span className="opp-pipeline-label">{stage.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {(summary?.onHold || 0) > 0 && (
                <p className="muted opp-pipeline-aside">
                  + {summary.onHold} on hold
                </p>
              )}
            </section>
          )}

          {/* Action Panels Row */}
          <div className="opp-action-row">
            {/* Urgent / Needs Attention */}
            <section className="panel-card opp-action-panel">
              <h3 className="opp-section-heading">
                Needs Your Attention
                {urgentItems.length > 0 && (
                  <span className="opp-attention-count">{urgentItems.length}</span>
                )}
              </h3>
              {urgentItems.length === 0 ? (
                <p className="muted opp-empty-action">All caught up — no urgent items right now.</p>
              ) : (
                <ul className="opp-urgent-list">
                  {urgentItems.map((opp) => {
                    const overdue = opp.nextFollowUpAt && isOverdue(opp.nextFollowUpAt);
                    return (
                      <li key={opp.opportunityId}>
                        <Link
                          to={paths.viewSection(clientViewId, "opportunities", opp.opportunityId)}
                          className={`opp-urgent-item${overdue ? " opp-urgent-item--overdue" : ""}`}
                        >
                          <div className="opp-urgent-left">
                            <span className={`opp-badge ${statusBadgeClass(opp.status)}`}>
                              {statusLabel(opp.status)}
                            </span>
                            <strong>{opp.contactName || "Unknown contact"}</strong>
                          </div>
                          <div className="opp-urgent-right">
                            {overdue ? (
                              <span className="opp-overdue-tag">Follow-up overdue</span>
                            ) : (
                              <span className="muted">{timeAgo(opp.submittedAt)}</span>
                            )}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Recent Activity */}
            <section className="panel-card opp-action-panel">
              <h3 className="opp-section-heading">Recent Activity</h3>
              {recentItems.length === 0 ? (
                <p className="muted opp-empty-action">No recent activity.</p>
              ) : (
                <ul className="opp-recent-list">
                  {recentItems.map((opp) => (
                    <li key={opp.opportunityId}>
                      <Link
                        to={paths.viewSection(clientViewId, "opportunities", opp.opportunityId)}
                        className="opp-recent-item"
                      >
                        <div className="opp-recent-left">
                          <strong>{opp.contactName || "Unknown"}</strong>
                          <span className="muted">{opp.opportunityType || "Lead"}</span>
                        </div>
                        <div className="opp-recent-right">
                          <span className={`opp-badge opp-badge--sm ${statusBadgeClass(opp.status)}`}>
                            {statusLabel(opp.status)}
                          </span>
                          <span className="muted">{timeAgo(opp.lastActivityAt || opp.submittedAt)}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* View toggle */}
          <div className="opp-view-toggle">
            <button
              type="button"
              className={activeView === "dashboard" ? "active" : ""}
              onClick={() => setActiveView("dashboard")}
            >
              Dashboard
            </button>
            <button
              type="button"
              className={activeView === "table" ? "active" : ""}
              onClick={() => setActiveView("table")}
            >
              All Records
            </button>
          </div>

          {activeView === "table" && (
            <>
              {/* Filters */}
              <section className="panel-card">
                <div className="client-portal-filters">
                  <label className="admin-users-field">
                    <span>Search</span>
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                      placeholder={
                        isMidpointMedia
                          ? "Name, email, role, city…"
                          : "Name, email, phone, summary…"
                      }
                    />
                  </label>
                  <label className="admin-users-field">
                    <span>Status</span>
                    <select
                      value={status}
                      onChange={(e) => { setPage(1); setStatus(e.target.value); }}
                    >
                      <option value="">All statuses</option>
                      {(data.filterOptions?.statuses || [])
                        .filter((opt) => !(isMidpointMedia && opt.key === "completed"))
                        .map((opt) => (
                        <option key={opt.key} value={opt.key}>
                          {isMidpointMedia ? formatMidpointMediaOpportunityStatus(opt.key) : opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-users-field">
                    <span>Type</span>
                    <select value={opportunityType} onChange={(e) => { setPage(1); setOpportunityType(e.target.value); }}>
                      <option value="">All types</option>
                      {typeFilters.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-users-field">
                    <span>Sort</span>
                    <select value={sort} onChange={(e) => setSort(e.target.value)}>
                      {SORT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-users-field client-portal-checkbox-field">
                    <span>Needs follow-up</span>
                    <input
                      type="checkbox"
                      checked={needsFollowUp}
                      onChange={(e) => { setPage(1); setNeedsFollowUp(e.target.checked); }}
                    />
                  </label>
                </div>
              </section>

              {/* Table */}
              {!loading && opportunities.length === 0 ? (
                <ClientPortalEmpty
                  title="No opportunities found"
                  message="No opportunities match your current filters."
                />
              ) : (
                <section className="panel-card client-opportunities-table-wrap">
                  <table className="client-opportunities-table">
                    <thead>
                      <tr>
                        <th>Contact</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>Submitted</th>
                        <th>Last Activity</th>
                        <th>Follow-Up</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {opportunities.map((opp) => {
                        const overdue = opp.nextFollowUpAt && isOverdue(opp.nextFollowUpAt);
                        return (
                          <tr key={opp.opportunityId} className={overdue ? "opp-row--overdue" : ""}>
                            <td>
                              <strong>{opp.contactName || "—"}</strong>
                              <br />
                              <span className="muted">{opp.contactEmail || opp.contactPhone || ""}</span>
                            </td>
                            <td>
                              <span className={`opp-badge ${statusBadgeClass(opp.status)}`}>
                                {statusLabel(opp.status)}
                              </span>
                            </td>
                            <td>
                              {opp.opportunityType || "—"}
                              {isHiringOpportunity(opp) && opp.roleType ? (
                                <>
                                  <br />
                                  <span className="muted">{opp.roleType}</span>
                                </>
                              ) : null}
                            </td>
                            <td>{formatClientDate(opp.submittedAt)}</td>
                            <td>{timeAgo(opp.lastActivityAt)}</td>
                            <td>
                              {opp.nextFollowUpAt ? (
                                <span className={overdue ? "opp-overdue-tag" : ""}>
                                  {formatClientDate(opp.nextFollowUpAt)}
                                </span>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                            <td>
                              <Link
                                to={paths.viewSection(clientViewId, "opportunities", opp.opportunityId)}
                                className="btn btn-secondary btn-sm"
                              >
                                View
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {data.pagination?.totalPages > 1 && (
                    <div className="client-portal-pagination">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        Previous
                      </button>
                      <span className="muted">
                        Page {data.pagination.page} of {data.pagination.totalPages}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={page >= data.pagination.totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </section>
              )}
            </>
          )}

          {activeView === "dashboard" && opportunities.length === 0 && (
            <ClientPortalEmpty
                  title="No opportunities yet"
                  message={
                    isMidpointMedia
                      ? "Job applications and other inquiries will appear here so you can work them through the pipeline."
                      : "When visitors submit leads on your public site, they'll appear here as opportunities for you to act on."
                  }
            />
          )}
        </>
      ) : null}
    </>
  );
};

export default ClientOpportunitiesListPage;
